import { useMemo, useCallback, useState, useEffect } from "react";
import {
  FORWARD_MODE_DYNAMIC,
  FORWARD_MODE_SIMPLE,
  getForwardMode,
  getForwardVmFallback,
  resolveForwardAction,
} from "../utils/forwardActionMode";
import {
  dialableDestinationCount,
  getMultiForwardProtectionUpdates,
  normalizeDestinations,
} from "../utils/forwardDestinations";

/**
 * Shared utilities for call action hooks
 */

/** Container class for call action UIs when used inside a call menu (no background — blends into parent) */
export const CALL_MENU_CONTAINER_CLASS = "space-y-4 mt-2 rounded-lg";

// Helper to get common styling values (used within hooks with useMemo)
const getCommonStylingValues = (context) => {
  const isIVR = context === 'ivrMenu' || context === 'offHoursIvrMenu' || context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours';
  
  return {
    containerClass: isIVR 
      ? "space-y-4 shadow-sm bg-gray-50 mt-2 rounded-lg"
      : "bg-white border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm",
    inputClass: isIVR
      ? "w-full bg-transparent border-b-[1px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      : "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
    headerSize: isIVR
      ? "font-semibold text-[15px]"
      : "font-bold text-[18px]",
    showDivider: isIVR,
    useIVRStyle: isIVR,
  };
};

// Options are stored as object keyed by DTMF digit { "1": {...}, "2": {...} }. Find option by id.
const getOptionFromOptions = (options, ivrOptionId) => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
  return Object.values(options).find((opt) => opt?.id === ivrOptionId) ?? null;
};

// Helper to get value from route based on context (optionKey when provided avoids lookup by id)
const getValueFromRoute = (context, route, ivrOptionId, fieldMap, optionKey = null) => {
  if (context === 'ivrMenu') {
    const option = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : getOptionFromOptions(route?.ivrMenu?.options, ivrOptionId);
    return option?.[fieldMap.ivr] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'ivrMenuOffHours') {
    const option = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : getOptionFromOptions(route?.ivrMenu?.options, ivrOptionId);
    return option?.[fieldMap.offHours] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'offHoursIvrMenuOffHours') {
    const option = getOptionFromOptions(route?.offHoursIvrMenu?.options, ivrOptionId);
    return option?.[fieldMap.offHours] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'offHoursIvrMenu') {
    const option = getOptionFromOptions(route?.offHoursIvrMenu?.options, ivrOptionId);
    return option?.[fieldMap.ivr] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'offHours') {
    return route?.[fieldMap.offHours] ?? fieldMap.defaultValue ?? "";
  } else {
    return route?.[fieldMap.root] ?? fieldMap.defaultValue ?? "";
  }
};

// Find option key ("1", "2", ...) by option id in options object
const getOptionKeyById = (options, ivrOptionId) => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
  const entry = Object.entries(options).find(([, opt]) => opt?.id === ivrOptionId);
  return entry ? entry[0] : null;
};

// Helper to update route based on context (options keyed by DTMF digit). optionKey when provided targets that key directly.
const updateRouteValue = (context, setRoute, ivrOptionId, fieldMap, value, additionalUpdates = {}, optionKey = null) => {
  if (context === 'ivrMenu') {
    setRoute((prev) => {
      const key = optionKey != null ? optionKey : getOptionKeyById(prev.ivrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.ivrMenu?.options ?? {};
      return {
        ...prev,
        ivrMenu: {
          ...prev.ivrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.ivr]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'ivrMenuOffHours') {
    setRoute((prev) => {
      const key = optionKey != null ? optionKey : getOptionKeyById(prev.ivrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.ivrMenu?.options ?? {};
      return {
        ...prev,
        ivrMenu: {
          ...prev.ivrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.offHours]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'offHoursIvrMenuOffHours') {
    setRoute((prev) => {
      const key = getOptionKeyById(prev.offHoursIvrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.offHoursIvrMenu?.options ?? {};
      return {
        ...prev,
        offHoursIvrMenu: {
          ...prev.offHoursIvrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.offHours]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'offHoursIvrMenu') {
    setRoute((prev) => {
      const key = getOptionKeyById(prev.offHoursIvrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.offHoursIvrMenu?.options ?? {};
      return {
        ...prev,
        offHoursIvrMenu: {
          ...prev.offHoursIvrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.ivr]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'offHours') {
    setRoute((prev) => ({
      ...prev,
      [fieldMap.offHours]: value,
      ...additionalUpdates,
    }));
  } else {
    setRoute((prev) => ({
      ...prev,
      [fieldMap.root]: value,
      ...additionalUpdates,
    }));
  }
};

const isIvrContext = (context) =>
  context === "ivrMenu" ||
  context === "offHoursIvrMenu" ||
  context === "ivrMenuOffHours" ||
  context === "offHoursIvrMenuOffHours";

const isIvrPrimaryContext = (context) =>
  context === "ivrMenu" || context === "offHoursIvrMenu";

const isIvrOffHoursContext = (context) =>
  context === "ivrMenuOffHours" || context === "offHoursIvrMenuOffHours";

export const DYNAMIC_FORWARD_BACKUP_NONE = "none";

export const DYNAMIC_FORWARD_PRIMARY_DEFAULTS = {
  twilanswerVmFallback: true,
  callerIdPref: "twilio",
  callApproval: "enabled",
};

export const DYNAMIC_FORWARD_OFF_HOURS_SLOT_DEFAULTS = {
  offHoursTwilanswerVmFallback: true,
  offHoursCallerIdPref: "twilio",
  callApproval: "enabled",
};

/** Fields to merge when selecting dynamic_forward in a route or IVR option */
export function getDynamicForwardInitForAction(action, isOffHoursSlot = false) {
  if (action !== "dynamic_forward") return {};
  return isOffHoursSlot
    ? { ...DYNAMIC_FORWARD_OFF_HOURS_SLOT_DEFAULTS }
    : { ...DYNAMIC_FORWARD_PRIMARY_DEFAULTS };
}

const getForwardSlotOption = (context, route, ivrOptionId, optionKey = null) => {
  if (context === "ivrMenu" || context === "ivrMenuOffHours") {
    return optionKey != null
      ? route?.ivrMenu?.options?.[optionKey]
      : getOptionFromOptions(route?.ivrMenu?.options, ivrOptionId);
  }
  if (context === "offHoursIvrMenu" || context === "offHoursIvrMenuOffHours") {
    return getOptionFromOptions(route?.offHoursIvrMenu?.options, ivrOptionId);
  }
  return null;
};

const isOffHoursSlot = (context) =>
  context === "offHours" || isIvrOffHoursContext(context);

const hasOwnField = (obj, key) =>
  obj != null && Object.prototype.hasOwnProperty.call(obj, key);

const normalizeCallerIdPref = (pref) => {
  if (pref === "original") return "caller";
  if (pref === "system") return "twilio";
  return pref || "caller";
};

// Effective values must resolve exactly like lambda/twilioCallReceiver/helpers.py
// (resolve_caller_id_pref, handle_call_action, and the IVR mock_route), so the
// UI never shows a setting that live calls won't use.
const resolveEffectiveCallerIdPref = (context, route, ivrOptionId, optionKey) => {
  const option = getForwardSlotOption(context, route, ivrOptionId, optionKey);
  const businessPref = option
    ? option.callerIdPref || route?.callerIdPref
    : route?.callerIdPref;
  if (isOffHoursSlot(context)) {
    const offHoursPref = option
      ? option.offHoursCallerIdPref || route?.offHoursCallerIdPref
      : route?.offHoursCallerIdPref;
    return normalizeCallerIdPref(offHoursPref || businessPref);
  }
  return normalizeCallerIdPref(businessPref);
};

const resolveEffectiveCallApproval = (context, route, ivrOptionId, optionKey) => {
  const option = getForwardSlotOption(context, route, ivrOptionId, optionKey);
  const value = hasOwnField(option, "callApproval")
    ? option.callApproval
    : route?.callApproval;
  return value || "disabled";
};

const resolveEffectiveVmFallback = (context, route, ivrOptionId, optionKey) => {
  const key = isOffHoursSlot(context)
    ? "offHoursTwilanswerVmFallback"
    : "twilanswerVmFallback";
  const option = getForwardSlotOption(context, route, ivrOptionId, optionKey);
  const raw = hasOwnField(option, key) ? option[key] : route?.[key];
  return raw === true || raw?.BOOL === true;
};

/** Read primaryAction / offHoursAction for the current forward slot. */
const getActionFromRoute = (context, route, ivrOptionId, optionKey = null) => {
  return getValueFromRoute(
    context,
    route,
    ivrOptionId,
    {
      ivr: "primaryAction",
      offHours: "offHoursAction",
      root: "primaryAction",
      defaultValue: "forward",
    },
    optionKey
  );
};

/** Write primaryAction / offHoursAction (and optional field patches) for the current slot. */
const setActionOnRoute = (
  context,
  setRoute,
  ivrOptionId,
  action,
  extraFields = {},
  optionKey = null
) => {
  if (isIvrPrimaryContext(context)) {
    updateRouteValue(
      context,
      setRoute,
      ivrOptionId,
      { ivr: "primaryAction", offHours: "offHoursAction", root: "primaryAction" },
      action,
      extraFields,
      optionKey
    );
  } else if (isIvrOffHoursContext(context)) {
    updateRouteValue(
      context,
      setRoute,
      ivrOptionId,
      { ivr: "primaryAction", offHours: "offHoursAction", root: "primaryAction" },
      action,
      extraFields,
      optionKey
    );
  } else if (context === "offHours") {
    setRoute((prev) => ({
      ...prev,
      offHoursAction: action,
      ...extraFields,
    }));
  } else {
    setRoute((prev) => ({
      ...prev,
      primaryAction: action,
      ...extraFields,
    }));
  }
};

/**
 * Unified Forward hook — maps Simple/Dynamic + VM toggle onto
 * forward / forwardvwm / dynamic_forward without changing stored field shapes.
 */
export const useForwardAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  const actionType = useMemo(() => {
    return getActionFromRoute(context, route, ivrOptionId, optionKey) || "forward";
  }, [context, route, ivrOptionId, optionKey]);

  const mode = useMemo(
    () => getForwardMode(actionType) || FORWARD_MODE_SIMPLE,
    [actionType]
  );

  const numberValue = useMemo(() => {
    return getValueFromRoute(
      context,
      route,
      ivrOptionId,
      {
        ivr: "primaryNumber",
        offHours: "offHoursNumber",
        root: "primaryNumber",
      },
      optionKey
    );
  }, [context, route, ivrOptionId, optionKey]);

  const destinationsRaw = useMemo(() => {
    return getValueFromRoute(
      context,
      route,
      ivrOptionId,
      {
        ivr: "primaryDestinations",
        offHours: "offHoursDestinations",
        root: "primaryDestinations",
        defaultValue: null,
      },
      optionKey
    );
  }, [context, route, ivrOptionId, optionKey]);

  /** Simultaneous ring list; falls back to [primaryNumber] for older routes. */
  const destinationValues = useMemo(
    () => normalizeDestinations(destinationsRaw, numberValue),
    [destinationsRaw, numberValue]
  );

  const callerIdPref = useMemo(
    () => resolveEffectiveCallerIdPref(context, route, ivrOptionId, optionKey),
    [context, route, ivrOptionId, optionKey]
  );

  const callApproval = useMemo(
    () => resolveEffectiveCallApproval(context, route, ivrOptionId, optionKey),
    [context, route, ivrOptionId, optionKey]
  );

  const recordingValue = useMemo(() => {
    return getValueFromRoute(
      context,
      route,
      ivrOptionId,
      {
        ivr: "primaryRecording",
        offHours: "offHoursRecording",
        root: "primaryRecording",
      },
      optionKey
    );
  }, [context, route, ivrOptionId, optionKey]);

  const twilanswerVmFallbackRaw = useMemo(
    () => resolveEffectiveVmFallback(context, route, ivrOptionId, optionKey),
    [context, route, ivrOptionId, optionKey]
  );

  const vmFallback = useMemo(
    () => getForwardVmFallback(actionType, twilanswerVmFallbackRaw),
    [actionType, twilanswerVmFallbackRaw]
  );

  const currentActionKey = isIvrOffHoursContext(context)
    ? "offHoursAction"
    : isIvrPrimaryContext(context)
      ? "primaryAction"
      : context === "offHours"
        ? "offHoursAction"
        : "primaryAction";

  const destinationsField = isOffHoursSlot(context)
    ? "offHoursDestinations"
    : "primaryDestinations";

  const handleDestinationsChange = useCallback(
    (phones) => {
      const cleaned = normalizeDestinations(phones, null);
      const first = cleaned.length > 0 ? cleaned[0] : null;
      const previousCount = dialableDestinationCount(destinationValues, null);
      const nextCount = dialableDestinationCount(cleaned, null);
      const isSimpleForward = getForwardMode(actionType) === FORWARD_MODE_SIMPLE;
      const protections = getMultiForwardProtectionUpdates({
        previousCount,
        nextCount,
        isSimpleForward,
      });

      let nextAction = actionType;
      if (protections.enableVmFallback) {
        nextAction = resolveForwardAction({
          mode: FORWARD_MODE_SIMPLE,
          vmFallback: true,
        });
      }

      const additionalUpdates = {
        [destinationsField]: cleaned.length > 0 ? cleaned : null,
        ...(isIvrContext(context) || protections.enableVmFallback
          ? { [currentActionKey]: nextAction }
          : {}),
        ...(protections.enableCallApproval ? { callApproval: "enabled" } : {}),
      };
      updateRouteValue(
        context,
        setRoute,
        ivrOptionId,
        {
          ivr: "primaryNumber",
          offHours: "offHoursNumber",
          root: "primaryNumber",
        },
        first,
        additionalUpdates,
        optionKey
      );

      if (context === "offHours") {
        setRoute((prev) => ({ ...prev, offHoursChannel: null }));
      } else if (context === "root") {
        setRoute((prev) => ({ ...prev, primaryChannel: null }));
      }
    },
    [
      context,
      setRoute,
      ivrOptionId,
      optionKey,
      actionType,
      currentActionKey,
      destinationsField,
      destinationValues,
    ]
  );

  /** Single-destination setter (dynamic backup / legacy). Keeps destinations[] in sync. */
  const handleNumberChange = useCallback(
    (value) => {
      if (value === DYNAMIC_FORWARD_BACKUP_NONE) {
        handleDestinationsChange([DYNAMIC_FORWARD_BACKUP_NONE]);
        return;
      }
      if (!value) {
        handleDestinationsChange([]);
        return;
      }
      handleDestinationsChange([value]);
    },
    [handleDestinationsChange]
  );

  const handleCallerIdChange = useCallback(
    (value) => {
      if (isIvrPrimaryContext(context)) {
        updateRouteValue(
          context,
          setRoute,
          ivrOptionId,
          {
            ivr: "callerIdPref",
            offHours: "callerIdPref",
            root: "callerIdPref",
          },
          value,
          {},
          optionKey
        );
      } else if (isIvrOffHoursContext(context)) {
        updateRouteValue(
          context,
          setRoute,
          ivrOptionId,
          {
            ivr: "callerIdPref",
            offHours: "offHoursCallerIdPref",
            root: "callerIdPref",
          },
          value,
          {},
          optionKey
        );
      } else {
        updateRouteValue(
          context,
          setRoute,
          ivrOptionId,
          {
            ivr: "callerIdPref",
            offHours: "offHoursCallerIdPref",
            root: "callerIdPref",
          },
          value,
          {},
          optionKey
        );
      }

      if (context === "offHours") {
        setRoute((prev) => ({ ...prev, offHoursChannel: null }));
      } else if (context === "root") {
        setRoute((prev) => ({ ...prev, primaryChannel: null }));
      }
    },
    [context, setRoute, ivrOptionId, optionKey]
  );

  const handleCallApprovalChange = useCallback(
    (value) => {
      if (isIvrContext(context)) {
        updateRouteValue(
          context,
          setRoute,
          ivrOptionId,
          {
            ivr: "callApproval",
            offHours: "callApproval",
            root: "callApproval",
          },
          value,
          {},
          optionKey
        );
      } else {
        setRoute((prev) => ({
          ...prev,
          callApproval: value,
          ...(context === "offHours"
            ? { offHoursChannel: null }
            : { primaryChannel: null }),
        }));
      }
    },
    [context, setRoute, ivrOptionId, optionKey]
  );

  const handleRecordingChange = useCallback(
    (value) => {
      updateRouteValue(
        context,
        setRoute,
        ivrOptionId,
        {
          ivr: "primaryRecording",
          offHours: "offHoursRecording",
          root: "primaryRecording",
        },
        value,
        {},
        optionKey
      );
    },
    [context, setRoute, ivrOptionId, optionKey]
  );

  const handleVmFallbackChange = useCallback(
    (checked) => {
      if (mode === FORWARD_MODE_DYNAMIC) {
        updateRouteValue(
          context,
          setRoute,
          ivrOptionId,
          {
            ivr: "twilanswerVmFallback",
            offHours: "offHoursTwilanswerVmFallback",
            root: "twilanswerVmFallback",
          },
          checked,
          {},
          optionKey
        );
        return;
      }

      const nextAction = resolveForwardAction({
        mode: FORWARD_MODE_SIMPLE,
        vmFallback: checked,
      });
      const extras = {};
      // Preserve recording/callApproval when turning VM on; nothing to clear on turn-off
      // (fields may linger but validation only requires them for forwardvwm).
      if (checked && !isIvrContext(context) && route?.callApproval == null) {
        extras.callApproval = "disabled";
      }
      setActionOnRoute(
        context,
        setRoute,
        ivrOptionId,
        nextAction,
        extras,
        optionKey
      );
    },
    [mode, context, setRoute, ivrOptionId, optionKey, route?.callApproval]
  );

  const handleModeChange = useCallback(
    (nextMode) => {
      if (nextMode === mode) return;

      if (nextMode === FORWARD_MODE_DYNAMIC) {
        const offHours = isOffHoursSlot(context);
        const callerIdKey = offHours ? "offHoursCallerIdPref" : "callerIdPref";
        const option = getForwardSlotOption(context, route, ivrOptionId, optionKey);
        const slotSource = isIvrContext(context) ? option : route;
        const defaults = offHours
          ? DYNAMIC_FORWARD_OFF_HOURS_SLOT_DEFAULTS
          : DYNAMIC_FORWARD_PRIMARY_DEFAULTS;
        const extras = {
          [offHours ? "offHoursTwilanswerVmFallback" : "twilanswerVmFallback"]:
            vmFallback,
          ...(slotSource?.[callerIdKey]
            ? {}
            : { [callerIdKey]: defaults[callerIdKey] }),
          ...(slotSource?.callApproval
            ? {}
            : { callApproval: defaults.callApproval }),
        };
        setActionOnRoute(
          context,
          setRoute,
          ivrOptionId,
          "dynamic_forward",
          extras,
          optionKey
        );
        return;
      }

      // Dynamic → Simple: "none" is not valid for simple forward
      const extras = {};
      const backupIsNone = !numberValue || numberValue === DYNAMIC_FORWARD_BACKUP_NONE;
      if (backupIsNone) {
        if (context === "offHours" || isIvrOffHoursContext(context)) {
          extras.offHoursNumber = null;
          extras.offHoursDestinations = null;
        } else {
          extras.primaryNumber = null;
          extras.primaryDestinations = null;
        }
      }
      const nextAction = resolveForwardAction({
        mode: FORWARD_MODE_SIMPLE,
        vmFallback,
      });
      setActionOnRoute(
        context,
        setRoute,
        ivrOptionId,
        nextAction,
        extras,
        optionKey
      );
    },
    [
      mode,
      context,
      route,
      setRoute,
      ivrOptionId,
      optionKey,
      vmFallback,
      numberValue,
    ]
  );

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  const callerIdOptions = useMemo(() => {
    return [
      { value: "caller", label: "Show Original Caller's Number" },
      {
        value: "twilio",
        label: isIvrContext(context)
          ? "Show Twilio Line Number"
          : "Show System Number",
      },
    ];
  }, [context]);

  return {
    actionType,
    mode,
    numberValue,
    destinationValues,
    callerIdPref,
    callApproval,
    recordingValue,
    vmFallback,
    twilanswerVmFallback: vmFallback,
    handleNumberChange,
    handleDestinationsChange,
    handleCallerIdChange,
    handleCallApprovalChange,
    handleRecordingChange,
    handleVmFallbackChange,
    handleTwilanswerVmFallbackChange: handleVmFallbackChange,
    handleModeChange,
    callerIdOptions,
    ...styling,
  };
};

/**
 * Twilanswer App action — rings a user's registered mobile devices via device:<userId>.
 * Optional voicemail recording: when set, twilanswerVmFallback is true; when cleared, false.
 */
export const useTwilanswerAppAction = (
  context,
  route,
  setRoute,
  ivrOptionId = null,
  optionKey = null
) => {
  const numberValue = useMemo(() => {
    return getValueFromRoute(
      context,
      route,
      ivrOptionId,
      {
        ivr: "primaryNumber",
        offHours: "offHoursNumber",
        root: "primaryNumber",
      },
      optionKey
    );
  }, [context, route, ivrOptionId, optionKey]);

  const recordingValue = useMemo(() => {
    return getValueFromRoute(
      context,
      route,
      ivrOptionId,
      {
        ivr: "primaryRecording",
        offHours: "offHoursRecording",
        root: "primaryRecording",
      },
      optionKey
    );
  }, [context, route, ivrOptionId, optionKey]);

  const twilanswerVmFallbackRaw = useMemo(() => {
    const raw = getValueFromRoute(
      context,
      route,
      ivrOptionId,
      {
        ivr: "twilanswerVmFallback",
        offHours: "offHoursTwilanswerVmFallback",
        root: "twilanswerVmFallback",
        defaultValue: false,
      },
      optionKey
    );
    return raw === true || raw?.BOOL === true;
  }, [context, route, ivrOptionId, optionKey]);

  const hasRecording = Boolean(recordingValue && recordingValue !== "disabled");
  const expectedVmFallback = hasRecording;

  // Keep twilanswerVmFallback in sync with whether a voicemail recording is set.
  useEffect(() => {
    if (twilanswerVmFallbackRaw === expectedVmFallback) return;
    updateRouteValue(
      context,
      setRoute,
      ivrOptionId,
      {
        ivr: "twilanswerVmFallback",
        offHours: "offHoursTwilanswerVmFallback",
        root: "twilanswerVmFallback",
      },
      expectedVmFallback,
      {},
      optionKey
    );
  }, [
    context,
    setRoute,
    ivrOptionId,
    optionKey,
    expectedVmFallback,
    twilanswerVmFallbackRaw,
  ]);

  const handleNumberChange = useCallback(
    (value) => {
      updateRouteValue(
        context,
        setRoute,
        ivrOptionId,
        {
          ivr: "primaryNumber",
          offHours: "offHoursNumber",
          root: "primaryNumber",
        },
        value,
        {},
        optionKey
      );

      if (context === "offHours") {
        setRoute((prev) => ({ ...prev, offHoursChannel: null }));
      } else if (context === "root") {
        setRoute((prev) => ({ ...prev, primaryChannel: null }));
      }
    },
    [context, setRoute, ivrOptionId, optionKey]
  );

  const handleRecordingChange = useCallback(
    (value) => {
      const nextRecording = value || null;
      const vmFallback = Boolean(nextRecording && nextRecording !== "disabled");
      const vmKey =
        context === "offHours" || isIvrOffHoursContext(context)
          ? "offHoursTwilanswerVmFallback"
          : "twilanswerVmFallback";
      updateRouteValue(
        context,
        setRoute,
        ivrOptionId,
        {
          ivr: "primaryRecording",
          offHours: "offHoursRecording",
          root: "primaryRecording",
        },
        nextRecording,
        { [vmKey]: vmFallback },
        optionKey
      );
    },
    [context, setRoute, ivrOptionId, optionKey]
  );

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  return {
    numberValue,
    recordingValue,
    twilanswerVmFallback: expectedVmFallback,
    handleNumberChange,
    handleRecordingChange,
    ...styling,
  };
};

export const useSendToVoicemailAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  // Get recording value based on context
  const recordingValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle recording change
  const handleRecordingChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  // SendToVoicemail has slightly different styling - empty container for IVR
  const containerClass = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return ""; // Empty - wrapper div handles styling
    } else {
      return "bg-white border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm";
    }
  }, [context]);

  const inputClass = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "mb-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
    } else {
      return "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
    }
  }, [context]);

  const headerSize = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "font-semibold text-[15px]"; // Match ForwardUI styling for consistency
    } else {
      return "font-bold text-[18px]";
    }
  }, [context]);

  const showDivider = context === 'ivrMenu' || context === 'offHoursIvrMenu';
  const useIVRStyle = context === 'ivrMenu' || context === 'offHoursIvrMenu';

  return {
    recordingValue,
    handleRecordingChange,
    containerClass,
    inputClass,
    headerSize,
    showDivider,
    useIVRStyle,
  };
};

/**
 * Shared hook for PlayRecording action component logic across all contexts
 */
export const usePlayRecordingAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  // Get recording value based on context
  const recordingValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle recording change
  const handleRecordingChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  return {
    recordingValue,
    handleRecordingChange,
    ...styling,
  };
};

/**
 * Shared hook for ElevenLabs action component logic across all contexts
 */
export const useElevenLabsAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  const [imgError, setImgError] = useState(false);

  // Get agent ID value based on context
  const agentIdValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryAgent',
      offHours: 'offHoursAgent',
      root: 'primaryAgent',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle agent ID change
  const handleAgentIdChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryAgent',
      offHours: 'offHoursAgent',
      root: 'primaryAgent',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  return {
    agentIdValue,
    handleAgentIdChange,
    imgError,
    setImgError,
    ...styling,
  };
};

/**
 * Builds agent options for ElevenLabs dropdown from org channels.
 * Enabled agents first (from agents.L), disabled agents last (from el_exclusion_list) with title on hover.
 */
export const useElevenLabsAgentOptions = (channels) => {
  return useMemo(() => {
    const byId = new Map();
    (channels || [])
      .filter((ch) => ch.M?.type?.S === "elevenlabs")
      .forEach((ch) => {
        const channelLabel = ch.M?.label?.S || "ElevenLabs";
        (ch.M?.agents?.L || []).forEach((item) => {
          const id = item.M?.id?.S ?? item.M?.agent_id?.S;
          const name = item.M?.name?.S ?? id ?? "Unknown";
          if (id && !byId.has(id)) {
            byId.set(id, { value: id, label: `${name} (${channelLabel})`, disabled: false });
          }
        });
        (ch.M?.el_exclusion_list?.L || []).forEach((item) => {
          const id = item.S ?? item.M?.id?.S;
          if (id && !byId.has(id)) {
            byId.set(id, {
              value: id,
              label: `${id} (disabled)`,
              disabled: true,
              title: "This agent is disabled in the channel settings. Enable it in Channels to use it.",
            });
          }
        });
      });
    const enabled = [...byId.values()].filter((o) => !o.disabled);
    const disabled = [...byId.values()].filter((o) => o.disabled);
    return [...enabled, ...disabled];
  }, [channels]);
};
